import { useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  TaskDaily02Icon,
  Route01Icon,
  Calendar03Icon,
  CheckListIcon,
  Target02Icon,
} from "@hugeicons/core-free-icons";
import { BoardPage } from "./BoardPage";
import { PathPage } from "./PathPage";
import { TimelinePage } from "./TimelinePage";
import { ChecklistPage } from "./ChecklistPage";
import { PipelinePage } from "./PipelinePage";
import type { Card, CardStatus, FilterState } from "../data/types";
import type { KpiTotals } from "../lib/boardUtils";
import type { RouteId } from "../lib/routes";
import type { IconSvgElement } from "@hugeicons/react";

type WorkTab = "board" | "path" | "timeline" | "checklist" | "pipeline";
type TimeFilter = "all" | "today" | "this_week" | "next_7_days" | "this_month";
type OrderFilter = "default" | "date_asc" | "date_desc" | "priority";

interface TabConfig {
  id: WorkTab;
  label: string;
  icon: IconSvgElement;
  shortcut: string;
}

const TABS: TabConfig[] = [
  { id: "board", label: "Kanban", icon: TaskDaily02Icon, shortcut: "2" },
  { id: "path", label: "Runbook", icon: Route01Icon, shortcut: "3" },
  { id: "timeline", label: "Timeline", icon: Calendar03Icon, shortcut: "4" },
  { id: "pipeline", label: "Pipeline", icon: Target02Icon, shortcut: "5" },
  { id: "checklist", label: "Checklist", icon: CheckListIcon, shortcut: "9" },
];

const TIME_FILTER_OPTIONS: Array<{ value: TimeFilter; label: string }> = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "this_week", label: "This week" },
  { value: "next_7_days", label: "Next 7 days" },
  { value: "this_month", label: "This month" },
];

const ORDER_FILTER_OPTIONS: Array<{ value: OrderFilter; label: string }> = [
  { value: "default", label: "Default order" },
  { value: "date_asc", label: "Date ↑" },
  { value: "date_desc", label: "Date ↓" },
  { value: "priority", label: "Priority" },
];

const PRIORITY_RANK: Record<Card["priority"], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseCardDate(value?: string): Date | null {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return startOfDay(parsed);
}

function filterCardsByTime(cards: Card[], timeFilter: TimeFilter): Card[] {
  if (timeFilter === "all") return cards;

  const now = new Date();
  const today = startOfDay(now);
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const next7End = new Date(today);
  next7End.setDate(today.getDate() + 6);

  return cards.filter((card) => {
    const cardDate = parseCardDate(card.targetDate);
    if (!cardDate) return false;

    switch (timeFilter) {
      case "today":
        return cardDate.getTime() === today.getTime();
      case "this_week":
        return cardDate >= weekStart && cardDate <= weekEnd;
      case "next_7_days":
        return cardDate >= today && cardDate <= next7End;
      case "this_month":
        return cardDate.getMonth() === today.getMonth() && cardDate.getFullYear() === today.getFullYear();
      default:
        return true;
    }
  });
}

function sortCardsByOrder(cards: Card[], orderFilter: OrderFilter): Card[] {
  if (orderFilter === "default") return cards;

  const sorted = [...cards];

  if (orderFilter === "date_asc") {
    sorted.sort((a, b) => {
      const aDate = parseCardDate(a.targetDate)?.getTime() ?? Number.POSITIVE_INFINITY;
      const bDate = parseCardDate(b.targetDate)?.getTime() ?? Number.POSITIVE_INFINITY;
      return aDate - bDate;
    });
    return sorted;
  }

  if (orderFilter === "date_desc") {
    sorted.sort((a, b) => {
      const aDate = parseCardDate(a.targetDate)?.getTime() ?? Number.NEGATIVE_INFINITY;
      const bDate = parseCardDate(b.targetDate)?.getTime() ?? Number.NEGATIVE_INFINITY;
      return bDate - aDate;
    });
    return sorted;
  }

  sorted.sort((a, b) => {
    const priorityDiff = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    if (priorityDiff !== 0) return priorityDiff;
    const aDate = parseCardDate(a.targetDate)?.getTime() ?? Number.POSITIVE_INFINITY;
    const bDate = parseCardDate(b.targetDate)?.getTime() ?? Number.POSITIVE_INFINITY;
    return aDate - bDate;
  });

  return sorted;
}

interface WorkBoardPageProps {
  activeTab: WorkTab;
  onTabChange: (tab: RouteId) => void;
  // Board & Timeline (filtered)
  filteredCards: Card[];
  // Path, Pipeline, Checklist (all)
  allCards: Card[];
  filters: FilterState;
  onFilterChange: (updates: Partial<FilterState>) => void;
  onResetFilters: () => void;
  onSelectCard: (cardId: string) => void;
  onStatusChange: (cardId: string, status: CardStatus) => void;
  // Pipeline
  kpiTotals: KpiTotals;
}

export function WorkBoardPage({
  activeTab,
  onTabChange,
  filteredCards,
  allCards,
  filters,
  onFilterChange,
  onResetFilters,
  onSelectCard,
  onStatusChange,
  kpiTotals,
}: WorkBoardPageProps) {
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("all");
  const [orderFilter, setOrderFilter] = useState<OrderFilter>("default");

  const timeFilteredCards = useMemo(
    () => filterCardsByTime(filteredCards, timeFilter),
    [filteredCards, timeFilter],
  );

  const timeFilteredAllCards = useMemo(
    () => filterCardsByTime(allCards, timeFilter),
    [allCards, timeFilter],
  );

  const orderedCards = useMemo(
    () => sortCardsByOrder(timeFilteredCards, orderFilter),
    [timeFilteredCards, orderFilter],
  );

  const orderedAllCards = useMemo(
    () => sortCardsByOrder(timeFilteredAllCards, orderFilter),
    [timeFilteredAllCards, orderFilter],
  );

  return (
    <div className="h-full flex flex-col">
      {/* Tab bar */}
      <div className="shrink-0 px-[var(--spacing-4)] pt-[var(--spacing-3)] pb-0 border-b border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)]">
        <div className="flex items-center gap-[var(--spacing-1)]">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={cn(
                  "relative flex items-center gap-[var(--spacing-2)] px-[var(--spacing-3)] py-[var(--spacing-2)] rounded-t-[var(--radius-md)]",
                  "text-[13px] font-medium cursor-pointer transition-colors duration-[var(--duration-fast)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
                  isActive
                    ? "text-[var(--color-text-primary)] bg-[var(--color-bg-canvas)]"
                    : "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]",
                )}
              >
                <HugeiconsIcon icon={tab.icon} size={16} />
                <span>{tab.label}</span>
                <kbd
                  className={cn(
                    "text-[10px] font-mono ml-[var(--spacing-1)] px-[4px] py-[1px] rounded-[3px]",
                    isActive
                      ? "text-[var(--color-text-muted)] bg-[var(--color-bg-hover)]"
                      : "text-[var(--color-text-muted)] bg-[var(--color-bg-surface)]",
                  )}
                >
                  {tab.shortcut}
                </kbd>
                {/* Active indicator line */}
                {isActive && (
                  <span className="absolute bottom-[-1px] left-[var(--spacing-2)] right-[var(--spacing-2)] h-[2px] bg-[var(--color-accent)] rounded-t-full" />
                )}
              </button>
            );
          })}

          <div className="ml-auto pb-[var(--spacing-2)] flex items-center gap-[var(--spacing-2)]">
            <select
              value={timeFilter}
              onChange={(event) => setTimeFilter(event.target.value as TimeFilter)}
              className={cn(
                "h-[32px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
                "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)] pr-[var(--spacing-6)]",
                "text-[13px] text-[var(--color-text-primary)] appearance-none cursor-pointer",
                "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
                "hover:border-[var(--color-bg-hover)] transition-colors duration-[var(--duration-fast)]",
              )}
              aria-label="Time filter"
            >
              {TIME_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={orderFilter}
              onChange={(event) => setOrderFilter(event.target.value as OrderFilter)}
              className={cn(
                "h-[32px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
                "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)] pr-[var(--spacing-6)]",
                "text-[13px] text-[var(--color-text-primary)] appearance-none cursor-pointer",
                "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
                "hover:border-[var(--color-bg-hover)] transition-colors duration-[var(--duration-fast)]",
              )}
              aria-label="Order filter"
            >
              {ORDER_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Active tab content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === "board" && (
          <BoardPage
            cards={orderedCards}
            filters={filters}
            onFilterChange={onFilterChange}
            onResetFilters={onResetFilters}
            onSelectCard={onSelectCard}
            onStatusChange={onStatusChange}
          />
        )}
        {activeTab === "path" && (
          <PathPage
            cards={orderedAllCards}
            onSelectCard={onSelectCard}
          />
        )}
        {activeTab === "timeline" && (
          <TimelinePage
            cards={orderedCards}
            filters={filters}
            onFilterChange={onFilterChange}
            onResetFilters={onResetFilters}
            onSelectCard={onSelectCard}
          />
        )}
        {activeTab === "pipeline" && (
          <PipelinePage
            cards={orderedAllCards}
            kpiTotals={kpiTotals}
            onSelectCard={onSelectCard}
          />
        )}
        {activeTab === "checklist" && (
          <ChecklistPage
            cards={orderedAllCards}
            onToggleCard={onStatusChange}
          />
        )}
      </div>
    </div>
  );
}
